import { useCallback, useEffect, useRef, useState } from "react"

export const COPY_FEEDBACK_DEFAULT_MS = 2000

/**
 * Copy text to clipboard and expose a transient `copied` flag for UI feedback
 * (e.g. a "Скопировано" popover/tooltip). The timer is per-instance (useRef),
 * so multiple consumers mounted simultaneously don't share/clear each other's
 * feedback window — which was a latent bug in the earlier module-level timeout
 * used by `InlineCode`.
 *
 * @param resetDelayMs how long `copied` stays true after a successful copy
 */
export function useCopyToClipboard(resetDelayMs = COPY_FEEDBACK_DEFAULT_MS) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const copy = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text)
      setCopied(true)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setCopied(false)
        timeoutRef.current = undefined
      }, resetDelayMs)
    },
    [resetDelayMs],
  )

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
    },
    [],
  )

  return { copied, copy }
}
